import type { AccentId, HistoryScope, ImportProgress, NotificationPrefs, UserSettings } from './types.js';

export function defaultSettings(timezone = 'UTC'): UserSettings {
  return {
    theme: 'system',
    fontScale: 1,
    timezone,
    globalPause: false,
    ring: { enabled: true, intervalMinutes: 60, ringtone: 'chime', volume: 0.8, ringDurationSeconds: 20, snoozeMinutes: 30 },
    quietHours: { enabled: false, start: '22:00', end: '07:00' },
    ai: { consentGivenAt: null, attachmentConsentGivenAt: null, excludedSenders: [], excludedTopics: [], redactPatterns: ['card_numbers', 'passwords'], builtinEngine: null },
    retentionDays: null,
    blockRemoteImages: true,
    lastInboxReviewAt: null,
    accent: 'ocean',
    sidebarStyle: 'light',
    density: 'comfortable',
    notifications: defaultNotificationPrefs(),
    notificationsSeenAt: null,
    onboardedAt: null,
    undoSendSeconds: 10,
    dailySummary: { enabled: false, hour: 8 },
  };
}

export function defaultNotificationPrefs(): NotificationPrefs {
  return { island: true, newMail: true, ai: true, automation: true, system: true, sound: true, desktopScreenIsland: true, durationSeconds: 6, islandPosition: 'center', islandGap: 'comfortable', islandStyle: 'black', islandMotion: 'smooth' };
}

/** Deep-merge stored settings over defaults so newly added fields always have values. */
export function mergeSettings(stored: Partial<UserSettings> | null | undefined, timezone?: string): UserSettings {
  const d = defaultSettings(timezone);
  if (!stored) return d;
  return {
    ...d,
    ...stored,
    ring: { ...d.ring, ...(stored.ring ?? {}) },
    quietHours: { ...d.quietHours, ...(stored.quietHours ?? {}) },
    ai: { ...d.ai, ...(stored.ai ?? {}) },
    notifications: { ...d.notifications, ...(stored.notifications ?? {}) },
    dailySummary: { ...d.dailySummary, ...(stored.dailySummary ?? {}) },
  };
}

export function defaultHistoryScope(): HistoryScope {
  return { mode: 'range', from: new Date(Date.now() - 90 * 86400_000).toISOString(), to: null, excludedFolderIds: [], excludedSenders: [] };
}

export function idleImport(): ImportProgress {
  return { phase: 'idle', processed: 0, estimatedTotal: null, oldestImportedAt: null, newestImportedAt: null, startedAt: null, completedAt: null, error: null };
}

/** Accent presets. `hex` is the light-theme primary; the web client derives the full palette in CSS. */
export const ACCENTS: { id: AccentId; label: string; hex: string }[] = [
  { id: 'ocean', label: 'Ocean', hex: '#1F6FEB' },
  { id: 'indigo', label: 'Indigo', hex: '#4F46E5' },
  { id: 'violet', label: 'Violet', hex: '#7C3AED' },
  { id: 'rose', label: 'Rose', hex: '#E11D48' },
  { id: 'sunset', label: 'Sunset', hex: '#EA580C' },
  { id: 'emerald', label: 'Emerald', hex: '#059669' },
  { id: 'teal', label: 'Teal', hex: '#0D9488' },
  { id: 'graphite', label: 'Graphite', hex: '#475569' },
];

export const WORKSPACE_COLORS = ['#4F46E5', '#1F6FEB', '#0D9488', '#059669', '#EA580C', '#E11D48', '#7C3AED', '#0F172A'];

export const ACCOUNT_COLORS = ['#1F6FEB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#64748B'];

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const MAX_PARSE_BYTES = 20 * 1024 * 1024;

/** Categories every workspace starts with. Keys are stable (rules and threads refer to them). */
export const BUILTIN_CATEGORIES: { key: string; name: string; color: string; description: string }[] = [
  { key: 'support', name: 'Support', color: '#0EA5E9', description: 'Customers asking for help, reporting problems or bugs, or following up on an issue.' },
  { key: 'sales', name: 'Sales', color: '#10B981', description: 'Quotes, pricing, proposals, orders, demos and new business enquiries.' },
  { key: 'billing', name: 'Billing', color: '#F59E0B', description: 'Invoices, payments, receipts, refunds, bank details and anything about money owed.' },
  { key: 'scheduling', name: 'Scheduling', color: '#8B5CF6', description: 'Meetings, calls, availability, calendar invitations and rescheduling.' },
  { key: 'recruiting', name: 'Recruiting', color: '#EC4899', description: 'Job applications, candidates, CVs, interviews and hiring.' },
  { key: 'partnership', name: 'Partnerships', color: '#14B8A6', description: 'Collaborations, partners, vendors and suppliers proposing to work together.' },
  { key: 'personal', name: 'Personal', color: '#E11D48', description: 'Personal messages from friends and family, not work.' },
  { key: 'newsletter', name: 'Newsletters', color: '#64748B', description: 'Newsletters, marketing, promotions and digests you subscribed to.' },
  { key: 'notification', name: 'Notifications', color: '#94A3B8', description: 'Automated notifications from apps and services: alerts, sign-ins, shipping, system messages.' },
  { key: 'other', name: 'Other', color: '#A1A1AA', description: 'Anything that fits nowhere else.' },
];
